import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useDeleteEmcComplianceFileSlotAttachment,
  useGetEmcComplianceFileSlot,
  useUploadEmcComplianceFileSlot,
} from '@/shared/hooks/api/emcProductComplianceFileAttachments'
import { mergeEmcTechnicalDrawingsEvidenceFiles } from '@/shared/lib/emcTechnicalDrawingsEvidence'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { ProductComplianceFileSlotStep } from '@/v2/components/product-compliance-briefing/ProductComplianceFileSlotStep'
import type { EmcBriefingStep } from '../../utils/emcLegislationBriefing'
import { V2EmcLegislationBriefingStepShell } from './V2EmcLegislationBriefingStepShell'

export function V2EmcTechnicalDrawingsSlotStep({
  step,
  index,
  productId,
}: {
  step: EmcBriefingStep
  index: number
  productId: string
}) {
  const { t } = useTranslation('common')
  const canWrite = useCanWriteOrgData()
  const evidenceSlotTitle = t(
    'v2.pages.products.detail.emcBriefing.steps.items.productDrawings.title'
  )
  const { data: drawings, isLoading: isDrawingsLoading } =
    useGetEmcComplianceFileSlot(productId, 'DRAWINGS_SCHEMAS')
  const { data: legacyDescriptions, isLoading: isLegacyLoading } =
    useGetEmcComplianceFileSlot(productId, 'DESCRIPTIONS_AND_EXPLANATIONS')
  const { mutateAsync: uploadFile } = useUploadEmcComplianceFileSlot(
    productId,
    'DRAWINGS_SCHEMAS'
  )
  const { mutateAsync: deleteDrawingsFile, isPending: isDeleteDrawingsPending } =
    useDeleteEmcComplianceFileSlotAttachment(productId, 'DRAWINGS_SCHEMAS')
  const {
    mutateAsync: deleteLegacyFile,
    isPending: isDeleteLegacyPending,
  } = useDeleteEmcComplianceFileSlotAttachment(
    productId,
    'DESCRIPTIONS_AND_EXPLANATIONS'
  )

  const files = mergeEmcTechnicalDrawingsEvidenceFiles(
    drawings,
    legacyDescriptions
  )
  const isLoading = isDrawingsLoading || isLegacyLoading
  const isDeletePending = isDeleteDrawingsPending || isDeleteLegacyPending

  const handleDeleteFile = useCallback(
    async (attachmentId: string) => {
      const match = files.find((file) => file.id === attachmentId)
      if (match == null) {
        return
      }
      if (match.deleteSlotKind === 'DRAWINGS_SCHEMAS') {
        await deleteDrawingsFile(attachmentId)
        return
      }
      await deleteLegacyFile(attachmentId)
    },
    [deleteDrawingsFile, deleteLegacyFile, files]
  )

  return (
    <ProductComplianceFileSlotStep
      canWrite={canWrite}
      isLoading={isLoading}
      files={files}
      isDeletePending={isDeletePending}
      evidenceSlotTitle={evidenceSlotTitle}
      onUploadFile={uploadFile}
      onDeleteFile={handleDeleteFile}
      stepShell={({ actions, children }) => (
        <V2EmcLegislationBriefingStepShell
          step={step}
          index={index}
          actions={actions}
        >
          {children}
        </V2EmcLegislationBriefingStepShell>
      )}
    />
  )
}
